import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { AccordionHoldings } from '@ovComponents/4-module/widgets/holdings/components/accordionHoldings';
import { calculatePrimaryAssetClassHoldings } from '@ovComponents/4-module/widgets/holdings/holdings.resources';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import {
  Box,
  Grid,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useToast,
} from 'hephaestus/web';
import { AccountBalanceWallet } from 'hephaestus/webIcons';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';

const showNoModelPortfolios = ({
  manuallyAssignModelPortfolios,
  themeId,
  modelPortfoliosThemeId,
  productCount,
}: {
  manuallyAssignModelPortfolios: boolean | undefined;
  themeId: string | null | undefined;
  modelPortfoliosThemeId: string | null | undefined;
  productCount: number;
}) =>
  !!manuallyAssignModelPortfolios &&
  !!themeId &&
  modelPortfoliosThemeId === themeId &&
  productCount === 0;

export const AssignPortfolioToSubAccountVisual = ({
  options,
  objectData,
  loading,
  updateObject,
  continueFunc,
  suggestedFinancialProduct,
  workflowCompletion,
  lockThemeSelection,
  manuallyAssignModelPortfolios,
  showTabs,
  tabsData,
  activeSubAccountIndex,
  setActiveSubAccountIndex,
  householdOwnsModel = false,
  errorMessage,
}: {
  options: any;
  objectData: any;
  loading: boolean;
  updateObject: {
    handleThemeChange: (themeId: string) => void;
    handleFinancialProductChange: (productId: string) => void;
  };
  continueFunc: any;
  suggestedFinancialProduct?: any;
  workflowCompletion?: any;
  lockThemeSelection?: boolean;
  manuallyAssignModelPortfolios?: boolean;
  showTabs?: boolean;
  tabsData?: any[];
  activeSubAccountIndex?: number;
  setActiveSubAccountIndex?: (index: number) => void;
  householdOwnsModel?: boolean;
  errorMessage?: string;
}) => {
  const { translateBackendString } = useLocalization();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['client', 'accountTypes', 'components']);
  const { enqueueToast } = useToast();
  const [assetClassHoldings, setAssetClassHoldings] = useState<any[]>([]);
  const notifiedModelPortfolioLoadFailures = useRef(new Set<string>());

  useEffect(() => {
    if (
      !objectData?.modelPortfolioLoadFailed ||
      !objectData?.id ||
      !objectData?.modelPortfolioLoadFailureVersion
    ) {
      return;
    }
    const failureKey = `${objectData.id}:${objectData.modelPortfolioLoadFailureVersion}`;
    if (notifiedModelPortfolioLoadFailures.current.has(failureKey)) return;
    notifiedModelPortfolioLoadFailures.current.add(failureKey);
    enqueueToast(t('shared:somethingWentWrong'), { variant: 'error' });
  }, [
    enqueueToast,
    objectData?.id,
    objectData?.modelPortfolioLoadFailed,
    objectData?.modelPortfolioLoadFailureVersion,
    t,
  ]);

  const submit = async () => {
    if (!objectData?.themeId) return;
    sendAnalytic(
      ovAnalyticsEvents.workflowsAssignPortfolioToSubAccountContinueButtonSelect,
      {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      },
    );
    await continueFunc();
  };

  useEffect(() => {
    if (!objectData?.financialProduct) {
      setAssetClassHoldings([]);
    }

    if (
      suggestedFinancialProduct &&
      !manuallyAssignModelPortfolios &&
      suggestedFinancialProduct.children
    ) {
      const holdings = calculatePrimaryAssetClassHoldings(
        suggestedFinancialProduct.children.map((x: any) => ({
          ...x,
          totalCents: x.percentage,
        })),
        100,
      );
      setAssetClassHoldings(holdings);
    }

    const financialProductChildren =
      objectData?.financialProduct?.children ??
      objectData?.availableFinancialProducts?.find(
        (financialProduct: any) =>
          financialProduct?.id === objectData?.financialProduct?.id,
      )?.children ??
      [];

    if (
      objectData?.financialProduct &&
      manuallyAssignModelPortfolios &&
      financialProductChildren.length > 0
    ) {
      const holdings = calculatePrimaryAssetClassHoldings(
        financialProductChildren.map((x: any) => ({
          ...x,
          totalCents: x.percentage,
        })),
        100,
      );
      setAssetClassHoldings(holdings);
    }
  }, [
    suggestedFinancialProduct,
    setAssetClassHoldings,
    manuallyAssignModelPortfolios,
    objectData?.financialProduct,
  ]);

  /* pre-select if there's exactly one theme available */
  // Only run once: use a ref to ensure this effect only runs a single time
  const hasPreselectedTheme = useRef(false);
  useEffect(() => {
    if (
      !hasPreselectedTheme.current &&
      objectData?.availableThemes?.length === 1 &&
      !objectData.themeId
    ) {
      hasPreselectedTheme.current = true;
      updateObject.handleThemeChange(objectData.availableThemes[0].id);
    }
  }, [objectData?.availableThemes, objectData?.themeId, updateObject]);

  if (!options) return <></>;

  if (householdOwnsModel) {
    return (
      <WorkflowCompletionForm
        onSubmit={async () => {
          await continueFunc();
        }}
        submitButtonProps={{
          label: t('continue'),
          disabled: loading,
          dataTestId: 'theme-selection-continue-button',
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackendString(options?.title)}
        </Typography>
        <Typography variant='bodyMedium' sx={{ mt: 2, mb: 3, fontWeight: 500 }}>
          {t('components:householdPortfolio.inheritedFromHousehold')}
        </Typography>
        {loading && (
          <Grid container spacing={2}>
            <Grid size={12}>
              <Skeleton width='100%' height='128px' variant='rounded' />
            </Grid>
          </Grid>
        )}
      </WorkflowCompletionForm>
    );
  }

  if (errorMessage) {
    return (
      <WorkflowCompletionForm
        onSubmit={async () => {}}
        submitButtonProps={{
          label: t('continue'),
          disabled: true,
          dataTestId: 'theme-selection-continue-button',
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackendString(options?.title)}
        </Typography>
        <Typography
          role='alert'
          variant='bodyLarge'
          color='error'
          sx={{ mt: 2, mb: 3 }}
        >
          {errorMessage}
        </Typography>
      </WorkflowCompletionForm>
    );
  }

  const selectedFinancialProduct =
    objectData?.availableFinancialProducts?.find(
      (fp: any) => fp.id === objectData?.financialProduct?.id,
    ) ?? suggestedFinancialProduct;

  const selectedProductUrl = translateBackendString(
    selectedFinancialProduct?.translatedUrl,
  );

  const showUrlModelPortfolioUrl =
    options?.displayModelPortfolioUrl && selectedProductUrl ? (
      <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
        {`[${t('components:portfolioFundFacts')}](${selectedProductUrl})`}
      </ReactMarkdown>
    ) : (
      <></>
    );

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled:
          loading ||
          !objectData.themeId ||
          (!!manuallyAssignModelPortfolios && !objectData.financialProduct),
        dataTestId: 'theme-selection-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackendString(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      {showTabs && tabsData && tabsData.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={activeSubAccountIndex || 0}
            onChange={(_event, index) =>
              (setActiveSubAccountIndex || (() => {}))(index)
            }
          >
            {tabsData.map((tab, index) => (
              <Tab
                key={index}
                value={index}
                label={translateBackendString(tab.label)}
                icon={tab.icon ? <AccountBalanceWallet /> : undefined}
                id={`tab-${index}`}
                aria-controls={`tabpanel-${index}`}
                data-testid={`tab-${index}`}
              />
            ))}
          </Tabs>
        </Box>
      )}
      {loading && (
        <Grid container spacing={2}>
          <Grid size={12}>
            <Skeleton width='100%' height='128px' variant='rounded' />
          </Grid>
          <Grid size={12}>
            <Skeleton width='100%' height='300px' variant='rounded' />
          </Grid>
        </Grid>
      )}
      {objectData?.availableThemes?.length > 0 && !loading && (
        <>
          <SelectField
            testId='theme-selection'
            disabled={lockThemeSelection || loading}
            onChange={(e: any) =>
              updateObject.handleThemeChange(e.target.value)
            }
            value={objectData.themeId || ''}
            label={translateBackendString(options?.themeLabel)}
            infoTooltip={additionalInfo(
              translateBackendString,
              options?.themeLabel?.additionalInfo,
            )}
            fullWidth
          >
            {objectData.availableThemes?.map((theme: any) => (
              <MenuItem
                data-testid={`theme-selection-${kebabCase(translateBackendString(theme.translatedName))}`}
                key={theme.id}
                value={theme.id}
              >
                {translateBackendString(theme.translatedName)}
              </MenuItem>
            ))}
          </SelectField>
          <Typography variant='bodyLarge' sx={{ mt: 2, mb: 3 }}>
            {translateBackendString(
              objectData.availableThemes?.find(
                (theme: any) => theme.id === objectData.themeId,
              )?.translatedDescription,
            )}
          </Typography>
          {manuallyAssignModelPortfolios && (
            <SelectField
              key={objectData.financialProduct?.id}
              testId='model-selection'
              disabled={loading}
              onChange={(e: any) =>
                updateObject.handleFinancialProductChange(e.target.value)
              }
              fullWidth
              value={objectData.financialProduct?.id || ''}
              label={translateBackendString(options?.modelPortfolioLabel)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.modelPortfolioLabel?.additionalInfo,
              )}
            >
              {objectData.availableFinancialProducts?.map((fp: any) => (
                <MenuItem
                  data-testid={`model-portfolio-${kebabCase(translateBackendString(fp.translatedName))}`}
                  key={fp.id}
                  value={fp.id}
                >
                  {translateBackendString(fp.translatedName)}{' '}
                  {!!objectData.userSuitability &&
                    suggestedFinancialProduct?.id === fp.id &&
                    ` (${t('components:changeTheme.suggested')})`}
                </MenuItem>
              ))}
            </SelectField>
          )}
          {showNoModelPortfolios({
            manuallyAssignModelPortfolios,
            themeId: objectData.themeId,
            modelPortfoliosThemeId: objectData.modelPortfoliosThemeId,
            productCount: objectData.availableFinancialProducts?.length ?? 0,
          }) && (
            <Typography
              variant='bodyMedium'
              sx={{ mt: 1 }}
              data-testid='model-portfolios-empty'
              role='status'
            >
              {t('components:changeTheme.noOptionsAvailable')}
            </Typography>
          )}
          {showUrlModelPortfolioUrl}
          <AccordionHoldings
            data-testid='accordion-holding'
            assetClassHoldings={assetClassHoldings}
            loading={loading}
            variant='outlined'
          />
        </>
      )}
    </WorkflowCompletionForm>
  );
};

export default AssignPortfolioToSubAccountVisual;
